import React, { useState } from 'react';
import { Outlet, NavLink, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { guideHref } from '../utils/guideLink';
import Logo from '../assets/logo';
import NotificationIcon from '../components/common/NotificationIcon';
import LanguageSwitcher from '../components/common/LanguageSwitcher';
import AppFooter from '../components/common/AppFooter';

// Styled components for layout
const LayoutContainer = styled.div`
  display: flex;
  min-height: 100vh;
  background-color: var(--app-bg);
`;

// Sidebar
const Sidebar = styled.aside`
  width: 260px;
  background-color: var(--app-sidebar);
  color: var(--text-inverse);
  display: flex;
  flex-direction: column;
  transition: transform var(--dur-media) var(--ease-control);
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  z-index: 100;
  /* The rail paints above the header (z-index 99); clip it so nothing inside can
     ever spill onto the header bar or the page title. */
  overflow: hidden;

  @media (max-width: 768px) {
    transform: translateX(${({ isOpen }) => (isOpen ? '0' : '-100%')});
    width: 240px;
  }
`;

/* The red masthead plate that carries the white wordmark, as in the admin rail. */
const SidebarHeader = styled.div`
  padding: 14px 20px;
  min-height: 76px;
  display: flex;
  align-items: center;
  background-color: var(--app-sidebar-plate);
  border-bottom: none;
`;

// The lockup is a raster <img> (825x170), so it must be sized here — the old
// `svg { ... }` rule matched nothing and let the logo render 194px wide.
const SidebarLogo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  min-width: 0;
  max-width: 100%;

  img,
  svg {
    height: 34px;
    width: auto;
    max-width: 100%;
    display: block;
  }
`;

// The app name is a subtitle UNDER the wordmark, never a second title beside it.
const SidebarLogoText = styled.div`
  font-family: ${({ theme }) => theme.typography.fontFamily.heading};
  font-size: 11px;
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  line-height: 1;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.85);
  margin-left: 0;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SidebarContent = styled.div`
  flex: 1;
  padding: ${({ theme }) => theme.spacing[4]} 0;
  overflow-y: auto;
`;

// Navigation menu
const NavMenu = styled.nav`
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const NavSection = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const NavSectionTitle = styled.div`
  font-size: var(--text-2xs);
  font-weight: var(--weight-bold);
  text-transform: uppercase;
  letter-spacing: 1px;
  color: rgba(255, 255, 255, 0.38);
  padding: 0 20px 10px;
`;

/* The current page is GREEN — the brand's own "you are here" rule. Nothing in
   the rail is a button, so the green reads as state, not as an approve action. */
const NavItem = styled(NavLink)`
  display: flex;
  align-items: center;
  padding: 11px 20px;
  color: var(--app-nav-idle);
  text-decoration: none;
  font-size: var(--text-base);
  font-weight: var(--weight-regular);
  transition: all var(--dur-control) var(--ease-control);
  border-left: 3px solid transparent;

  &:hover {
    background-color: rgba(255, 255, 255, 0.05);
    color: var(--app-nav-hover);
  }

  &.active {
    background-color: rgba(255, 255, 255, 0.06);
    color: var(--app-nav-active);
    font-weight: var(--weight-bold);
    border-left-color: var(--app-nav-active);
  }

  &:focus-visible {
    outline: none;
    box-shadow: inset var(--focus-ring-accent);
  }

  svg {
    margin-right: 12px;
    width: 18px;
    height: 18px;
  }

  @media (max-width: 768px) {
    padding: 14px 20px;
  }

  /* Touch has no hover, so the green affordance must fire on press instead. */
  @media (hover: none) {
    &:hover {
      background-color: transparent;
      color: var(--app-nav-idle);
    }

    &:active {
      background-color: rgba(255, 255, 255, 0.05);
      color: var(--app-nav-hover);
    }
  }
`;

// Content area
const Content = styled.div`
  flex: 1;
  margin-left: 260px;
  min-width: 0;

  @media (max-width: 768px) {
    margin-left: 0;
  }
`;

const Header = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[6]}`};
  background-color: var(--app-surface);
  /* The brand puts no elevation on chrome — a hairline, not a drop shadow. */
  border-bottom: 1px solid var(--app-hairline);
  box-shadow: none;
  position: sticky;
  top: 0;
  z-index: 99;

  @media (max-width: 768px) {
    padding: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[3]}`};
  }
`;

const PageTitle = styled.h1`
  font-size: var(--text-2xl);
  font-weight: var(--weight-bold);
  text-transform: uppercase;
  letter-spacing: var(--tracking-none);
  line-height: var(--leading-tight);
  color: var(--app-heading);
  margin: 0;

  @media (max-width: 768px) {
    font-size: var(--text-md);
  }
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
`;

const UserName = styled.div`
  margin-right: ${({ theme }) => theme.spacing[3]};
  font-size: var(--text-base);
  font-weight: var(--weight-regular);
  color: var(--app-text);

  @media (max-width: 576px) {
    display: none;
  }
`;

const UserMenu = styled.div`
  position: relative;
`;

const UserMenuButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.primary};
  color: white;
  border: none;
  cursor: pointer;
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};

  &:hover {
    background-color: ${({ theme }) => theme.colors.primaryDark};
  }
`;

const UserMenuDropdown = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: ${({ theme }) => theme.spacing[2]};
  width: 200px;
  background-color: ${({ theme }) => theme.colors.white};
  box-shadow: ${({ theme }) => theme.shadows.md};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  overflow: hidden;
  z-index: 10;
  display: ${({ isOpen }) => (isOpen ? 'block' : 'none')};
`;

const UserMenuItem = styled.button`
  display: block;
  width: 100%;
  text-align: left;
  padding: ${({ theme }) => theme.spacing[3]};
  background-color: transparent;
  color: ${({ theme }) => theme.colors.dark};
  border: none;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.default};

  &:hover {
    background-color: ${({ theme }) => theme.colors.light};
  }

  &:last-child {
    border-bottom: none;
  }

  &.logout {
    color: ${({ theme }) => theme.colors.error};
  }
`;

const MainContent = styled.main`
  padding: ${({ theme }) => theme.spacing[6]};
  background-color: var(--app-bg);
  min-height: calc(100vh - 70px); /* Adjust based on header height */

  @media (max-width: 768px) {
    padding-left: ${({ theme }) => theme.spacing[3]};
    padding-right: ${({ theme }) => theme.spacing[3]};
    /* Clear the fixed bottom tab bar (and calendar FAB) */
    padding-bottom: calc(76px + env(safe-area-inset-bottom));
  }
`;

// Mobile menu toggle
const MobileMenuToggle = styled.button`
  display: none;
  background: none;
  border: none;
  border-radius: var(--app-radius-control);
  color: var(--app-heading);
  cursor: pointer;
  padding: ${({ theme }) => theme.spacing[2]};
  margin-right: ${({ theme }) => theme.spacing[2]};

  &:hover {
    background: var(--surface-subtle);
    color: var(--act-primary);
    box-shadow: none;
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--focus-act);
  }

  svg {
    width: 24px;
    height: 24px;
  }

  @media (max-width: 768px) {
    display: block;
  }
`;

// Overlay for mobile
const Overlay = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: ${({ isOpen }) => (isOpen ? 'block' : 'none')};
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 99;
  }
`;

// Bottom tab bar (mobile only)
const BottomTabBar = styled.nav`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    height: calc(56px + env(safe-area-inset-bottom));
    padding-bottom: env(safe-area-inset-bottom);
    background-color: var(--app-surface);
    border-top: 1px solid var(--app-hairline);
    z-index: 98;
  }
`;

const BottomTabItem = styled(NavLink)`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  min-height: 44px;
  padding: ${({ theme }) => theme.spacing[1]} ${({ theme }) => theme.spacing[1]};
  color: var(--app-muted);
  text-decoration: none;
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  line-height: ${({ theme }) => theme.typography.lineHeight.none};

  svg {
    width: 22px;
    height: 22px;
    flex-shrink: 0;
  }

  span {
    max-width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  /* On white the current tab stays blue: the rail's green needs a dark ground. */
  &.active {
    color: var(--act-primary);
  }
`;

// Dashboard icon
const DashboardIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="7" height="7"></rect>
    <rect x="14" y="3" width="7" height="7"></rect>
    <rect x="14" y="14" width="7" height="7"></rect>
    <rect x="3" y="14" width="7" height="7"></rect>
  </svg>
);

// Time Entry icon
const TimeEntryIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"></circle>
    <polyline points="12 6 12 12 16 14"></polyline>
  </svg>
);

// Calendar icon
const CalendarIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
    <line x1="16" y1="2" x2="16" y2="6"></line>
    <line x1="8" y1="2" x2="8" y2="6"></line>
    <line x1="3" y1="10" x2="21" y2="10"></line>
  </svg>
);

// Profile icon
const ProfileIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
    <circle cx="12" cy="7" r="4"></circle>
  </svg>
);

// Menu icon for mobile toggle
const MenuIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="3" y1="12" x2="21" y2="12"></line>
    <line x1="3" y1="6" x2="21" y2="6"></line>
    <line x1="3" y1="18" x2="21" y2="18"></line>
  </svg>
);

const EmployeeLayout = () => {
  const { t, i18n } = useTranslation();
  const { currentUser } = useAuth();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);

  // Get page title based on current route
  const getPageTitle = () => {
    const path = location.pathname;

    if (path.includes('/employee/week')) return t('nav.employee.weeklyEntry');
    if (path.includes('/employee/dashboard')) return t('nav.employee.dashboard');
    if (path.includes('/employee/time-entries')) return t('nav.employee.timeEntries');
    if (path.includes('/employee/calendar')) return t('nav.employee.calendar');
    if (path.includes('/employee/profile')) return t('nav.employee.profile');

    return t('nav.employee.portal');
  };

  // Close sidebar when clicking outside on mobile
  const handleOverlayClick = () => {
    setIsSidebarOpen(false);
  };

  // User initials for avatar
  const getUserInitials = () => {
    if (!currentUser) return 'U';

    const firstName = currentUser.firstName || '';
    const lastName = currentUser.lastName || '';

    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  return (
    <LayoutContainer>
      {/* Sidebar */}
      <Sidebar isOpen={isSidebarOpen}>
        <SidebarHeader>
          <SidebarLogo>
            <Logo height={34} />
            <SidebarLogoText>{t('common.appName')}</SidebarLogoText>
          </SidebarLogo>
        </SidebarHeader>

        <SidebarContent>
          <NavMenu>
            <NavSection>
              <NavSectionTitle>{t('nav.section.main')}</NavSectionTitle>
              <NavItem to="/employee/calendar" onClick={() => setIsSidebarOpen(false)}>
                <CalendarIcon />
                {t('nav.employee.calendar')}
              </NavItem>
              <NavItem to="/employee/dashboard" onClick={() => setIsSidebarOpen(false)}>
                <DashboardIcon />
                {t('nav.employee.dashboard')}
              </NavItem>
            </NavSection>

            <NavSection>
              <NavSectionTitle>{t('nav.section.account')}</NavSectionTitle>
              <NavItem to="/employee/profile" onClick={() => setIsSidebarOpen(false)}>
                <ProfileIcon />
                {t('nav.employee.profile')}
              </NavItem>
              <NavItem to="/employee/notification-preferences" onClick={() => setIsSidebarOpen(false)}>
                <ProfileIcon />
                {t('nav.employee.notifications')}
              </NavItem>
              <NavItem as="a" href={guideHref('employee', i18n.language)} target="_blank" rel="noopener noreferrer" onClick={() => setIsSidebarOpen(false)}>
                <ProfileIcon />
                {t('common.userGuide')}
              </NavItem>
              <NavItem as="a" href="/legal" target="_blank" rel="noopener noreferrer" onClick={() => setIsSidebarOpen(false)}>
                <ProfileIcon />
                {t('legal.navLabel')}
              </NavItem>
            </NavSection>
          </NavMenu>
        </SidebarContent>
      </Sidebar>

      {/* Mobile overlay */}
      <Overlay isOpen={isSidebarOpen} onClick={handleOverlayClick} />

      {/* Content area */}
      <Content>
        <Header>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <MobileMenuToggle onClick={() => setIsSidebarOpen(!isSidebarOpen)}>
              <MenuIcon />
            </MobileMenuToggle>
            <PageTitle>{getPageTitle()}</PageTitle>
          </div>

          <UserInfo>
            <LanguageSwitcher />

            <NotificationIcon />

            <UserName>
              {currentUser?.firstName} {currentUser?.lastName}
            </UserName>
          </UserInfo>
        </Header>

        <MainContent>
          <Outlet />
        </MainContent>

        <AppFooter />
      </Content>

      {/* Mobile bottom tab bar */}
      <BottomTabBar>
        <BottomTabItem to="/employee/calendar">
          <CalendarIcon />
          <span>{t('nav.employee.calendar')}</span>
        </BottomTabItem>
        <BottomTabItem to="/employee/dashboard">
          <DashboardIcon />
          <span>{t('nav.employee.dashboard')}</span>
        </BottomTabItem>
        <BottomTabItem to="/employee/profile">
          <ProfileIcon />
          <span>{t('nav.employee.profile')}</span>
        </BottomTabItem>
      </BottomTabBar>
    </LayoutContainer>
  );
};

export default EmployeeLayout;